import {useEffect, useMemo} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useParams} from "react-router";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {calculateRemSize as crs, flexAbsoluteCenter} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {SelectFieldControlled} from "@pg-design/react-hook-forms-utils";
import {IRankingAnnualParams, rpAppLink} from "@pg-mono/rp-routes";

import {useGetRegionListQuery} from "../../../region/api/get_region_list";
import {generalRankingKeysType, rankingLastYear, rankingYears} from "../../constants/ranking";
import {rankingSelectionTracking} from "../../tracking/ranking_selection_tracking";
import {IOfferRankingCityOptionValues, IOfferRankingOptionValue} from "../../types/IOfferRankingFormOptionValue";
import {getRankingCitiesIdByYear} from "../../utils/get_ranking_cities_ids";
import {getOfferRankingFormCityOptions} from "./utils/get_offer_ranking_form_city_options";
import {getOfferRankingFormPeriodOptions} from "./utils/get_offer_ranking_form_period_options";

interface IProps {
    horizontal?: boolean;
}

interface IOfferRankingFormValues {
    period: IOfferRankingOptionValue;
    city: IOfferRankingCityOptionValues;
}

export function OfferRankingForm(props: IProps) {
    const {horizontal} = props;
    const {citySlug, year} = useParams<IRankingAnnualParams>();
    const citiesIds = getRankingCitiesIdByYear();

    const {data: regionList} = useGetRegionListQuery({region: citiesIds});

    const form = useForm<IOfferRankingFormValues>({
        resolver: yupResolver(
            Yup.object().shape({
                period: Yup.object().required("to pole jest wymagane"),
                city: Yup.object().required("to pole jest wymagane")
            })
        ),
        defaultValues: {
            period: {
                value: "2025",
                label: "2025"
            }
        }
    });

    const {handleSubmit, reset, resetField, watch, setError} = form;
    const selectedPeriod = watch("period");
    const selectedCity = watch("city");

    useEffect(() => {
        if (regionList) {
            const initialPeriodOptions = getOfferRankingFormPeriodOptions();
            const initialCityOptions = getOfferRankingFormCityOptions({year: year, regionList});

            reset(getInitialFormValues(year, citySlug, initialPeriodOptions, initialCityOptions));
        }
    }, [regionList, citySlug]);

    useEffect(() => {
        if (regionList && selectedCity) {
            const selectedRegionData = regionList.find((region) => region.slug === selectedCity?.value);
            const regionIsInPeriod =
                selectedRegionData?.id && getRankingCitiesIdByYear(selectedPeriod.value as generalRankingKeysType).includes(selectedRegionData.id);
            if (!regionIsInPeriod) {
                resetField("city", {defaultValue: {label: "Wybierz", value: undefined}});
            }
        }
    }, [selectedPeriod]);

    const onSubmit = (formValues: IOfferRankingFormValues) => {
        if (formValues.city.value) {
            rankingSelectionTracking({city: formValues.city.label, year: year || rankingLastYear.toString(), isForm: true});
            window.location.href = getRankingListUrl(formValues.period.value, formValues.city.value);
        } else {
            setError("city", {message: "Wybierz miasto"});
        }
    };

    const periodOptions = getOfferRankingFormPeriodOptions();
    const cityOptions = useMemo(() => getOfferRankingFormCityOptions({year: selectedPeriod?.value, regionList}), [selectedPeriod]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
                {regionList ? (
                    <FormContent horizontal={horizontal}>
                        <FormSelect horizontal={horizontal} name="period" options={periodOptions} />
                        <FormSelect horizontal={horizontal} name="city" options={cityOptions} />
                        <FormButton horizontal={horizontal} variant="filled_primary" type="submit" size="small">
                            Pokaż ranking
                        </FormButton>
                    </FormContent>
                ) : (
                    <div css={flexAbsoluteCenter}>
                        <Loader />
                    </div>
                )}
            </form>
        </FormProvider>
    );
}

// Utils
function getInitialFormValues(
    year: string | undefined,
    city: string | undefined,
    periodOptions: IOfferRankingOptionValue[],
    cityOptions: IOfferRankingOptionValue[]
): IOfferRankingFormValues | undefined {
    const initialPeriod = getInitialPeriodValue(year, city, periodOptions);
    const initialCity = city ? cityOptions.find((cityOption) => cityOption.value === city) : undefined;

    if (initialPeriod && initialCity) {
        return {
            period: initialPeriod,
            city: initialCity
        };
    }

    return undefined;
}

function getInitialPeriodValue(
    year: string | undefined,
    city: string | undefined,
    periodOptions: IOfferRankingOptionValue[]
): IOfferRankingOptionValue | undefined {
    if (year) {
        return periodOptions.find((periodOption) => periodOption.value.toString() === year);
    }

    if (city && !year) {
        return periodOptions.find((periodOption) => {
            return periodOption.value.toString() === rankingLastYear.toString();
        });
    }

    return undefined;
}

function getRankingListUrl(year: string, city: string) {
    const numPeriod = parseInt(year);

    if (rankingYears.includes(numPeriod) && numPeriod === rankingLastYear) {
        return rpAppLink.ranking.lastYearList({citySlug: city});
    }

    return rpAppLink.ranking.annualList({citySlug: city, year});
}

// Styles
const FormContent = styled.div<{horizontal?: boolean}>`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    ${({horizontal}) => css`
        flex-direction: ${horizontal ? "row" : "column"};
        gap: ${horizontal ? crs(3) : crs(2)};
    `}
`;

const FormSelect = styled(SelectFieldControlled)<{horizontal?: boolean}>`
    width: 100%;

    ${({horizontal}) =>
        horizontal
            ? css`
                  flex: 1 1 30%;
              `
            : ""}
`;

const FormButton = styled(Button)<{horizontal?: boolean}>`
    width: 100%;
    flex: 0 0 auto;

    ${({horizontal}) =>
        horizontal
            ? css`
                  max-width: 16.8rem;
              `
            : ""}
`;
import {useEffect, useMemo} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useParams} from "react-router";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {calculateRemSize as crs, flexAbsoluteCenter} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {SelectFieldControlled} from "@pg-design/react-hook-forms-utils";
import {IRankingAnnualParams, rpAppLink} from "@pg-mono/rp-routes";

import {useGetRegionListQuery} from "../../../region/api/get_region_list";
import {generalRankingKeysType, rankingLastYear, rankingYears} from "../../constants/ranking";
import {rankingSelectionTracking} from "../../tracking/ranking_selection_tracking";
import {IOfferRankingCityOptionValues, IOfferRankingOptionValue} from "../../types/IOfferRankingFormOptionValue";
import {getRankingCitiesIdByYear} from "../../utils/get_ranking_cities_ids";
import {getOfferRankingFormCityOptions} from "./utils/get_offer_ranking_form_city_options";
import {getOfferRankingFormPeriodOptions} from "./utils/get_offer_ranking_form_period_options";

interface IProps {
    horizontal?: boolean;
}

interface IOfferRankingFormValues {
    period: IOfferRankingOptionValue;
    city: IOfferRankingCityOptionValues;
}

export function OfferRankingForm(props: IProps) {
    const {horizontal} = props;
    const {citySlug, year} = useParams<IRankingAnnualParams>();
    const citiesIds = getRankingCitiesIdByYear();

    const {data: regionList} = useGetRegionListQuery({region: citiesIds});

    const form = useForm<IOfferRankingFormValues>({
        resolver: yupResolver(
            Yup.object().shape({
                period: Yup.object().required("to pole jest wymagane"),
                city: Yup.object().required("to pole jest wymagane")
            })
        ),
        defaultValues: {
            period: {
                value: "2025",
                label: "2025"
            }
        }
    });

    const {handleSubmit, reset, resetField, watch, setError} = form;
    const selectedPeriod = watch("period");
    const selectedCity = watch("city");

    useEffect(() => {
        if (regionList) {
            const initialPeriodOptions = getOfferRankingFormPeriodOptions();
            const initialCityOptions = getOfferRankingFormCityOptions({year: year, regionList});

            reset(getInitialFormValues(year, citySlug, initialPeriodOptions, initialCityOptions));
        }
    }, [regionList, citySlug]);

    useEffect(() => {
        if (regionList && selectedCity) {
            const selectedRegionData = regionList.find((region) => region.slug === selectedCity?.value);
            const regionIsInPeriod =
                selectedRegionData?.id && getRankingCitiesIdByYear(selectedPeriod.value as generalRankingKeysType).includes(selectedRegionData.id);
            if (!regionIsInPeriod) {
                resetField("city", {defaultValue: {label: "Wybierz", value: undefined}});
            }
        }
    }, [selectedPeriod]);

    const onSubmit = (formValues: IOfferRankingFormValues) => {
        if (formValues.city.value) {
            rankingSelectionTracking({city: formValues.city.label, year: year || rankingLastYear.toString(), isForm: true});
            window.location.href = getRankingListUrl(formValues.period.value, formValues.city.value);
        } else {
            setError("city", {message: "Wybierz miasto"});
        }
    };

    const periodOptions = getOfferRankingFormPeriodOptions();
    const cityOptions = useMemo(() => getOfferRankingFormCityOptions({year: selectedPeriod?.value, regionList}), [selectedPeriod]);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
                {regionList ? (
                    <FormContent horizontal={horizontal}>
                        <FormSelect horizontal={horizontal} name="period" options={periodOptions} />
                        <FormSelect horizontal={horizontal} name="city" options={cityOptions} />
                        <FormButton horizontal={horizontal} variant="filled_primary" type="submit" size="small">
                            Pokaż ranking
                        </FormButton>
                    </FormContent>
                ) : (
                    <div css={flexAbsoluteCenter}>
                        <Loader />
                    </div>
                )}
            </form>
        </FormProvider>
    );
}

// Utils
function getInitialFormValues(
    year: string | undefined,
    city: string | undefined,
    periodOptions: IOfferRankingOptionValue[],
    cityOptions: IOfferRankingOptionValue[]
): IOfferRankingFormValues | undefined {
    const initialPeriod = getInitialPeriodValue(year, city, periodOptions);
    const initialCity = city ? cityOptions.find((cityOption) => cityOption.value === city) : undefined;

    if (initialPeriod && initialCity) {
        return {
            period: initialPeriod,
            city: initialCity
        };
    }

    return undefined;
}

function getInitialPeriodValue(
    year: string | undefined,
    city: string | undefined,
    periodOptions: IOfferRankingOptionValue[]
): IOfferRankingOptionValue | undefined {
    if (year) {
        return periodOptions.find((periodOption) => periodOption.value.toString() === year);
    }

    if (city && !year) {
        return periodOptions.find((periodOption) => {
            return periodOption.value.toString() === rankingLastYear.toString();
        });
    }

    return undefined;
}

function getRankingListUrl(year: string, city: string) {
    const numPeriod = parseInt(year);

    if (rankingYears.includes(numPeriod) && numPeriod === rankingLastYear) {
        return rpAppLink.ranking.lastYearList({citySlug: city});
    }

    return rpAppLink.ranking.annualList({citySlug: city, year});
}

// Styles
const FormContent = styled.div<{horizontal?: boolean}>`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    ${({horizontal}) => css`
        flex-direction: ${horizontal ? "row" : "column"};
        gap: ${horizontal ? crs(3) : crs(2)};
    `}
`;

const FormSelect = styled(SelectFieldControlled)<{horizontal?: boolean}>`
    width: 100%;

    ${({horizontal}) =>
        horizontal
            ? css`
                  flex: 1 1 30%;
              `
            : ""}
`;

const FormButton = styled(Button)<{horizontal?: boolean}>`
    width: 100%;
    flex: 0 0 auto;

    ${({horizontal}) =>
        horizontal
            ? css`
                  max-width: 16.8rem;
              `
            : ""}
`;
